import axios from 'axios';
import {
    cloneDeep,
    isFunction,
} from 'lodash';
import { translate } from '../translations';
import { inboundTransformConfig, outboundTransformConfig } from './transformConfig';

import * as httpErrorCodes from './httpErrorCodes';
import { POST } from './httpMethods';
import isHTML from '../transforms/validators/validators';
import { APPLICATION_JSON, CONTENT_TYPE } from './headers';

const DEFAULT_TIMEOUT = 10000;
const ERROR_CODES = [
    httpErrorCodes.HTTP_STATUS_400,
    httpErrorCodes.HTTP_STATUS_401,
    httpErrorCodes.HTTP_STATUS_403,
    httpErrorCodes.HTTP_STATUS_404,
    httpErrorCodes.HTTP_STATUS_409,
    httpErrorCodes.HTTP_STATUS_500,
    httpErrorCodes.HTTP_STATUS_502,
    httpErrorCodes.HTTP_STATUS_503,
    httpErrorCodes.HTTP_STATUS_504,
    httpErrorCodes.HTTP_STATUS_507,
];

const generateErrorResponse = (message, statusCode) => ({
    error: message,
    statusCode,
});

const prepareBaseRequest = (config) => {
    const preparedConfig = cloneDeep(config);
    if (!preparedConfig.timeout) {
        preparedConfig.timeout = DEFAULT_TIMEOUT;
    }
    if (!preparedConfig.headers) {
        preparedConfig.headers = {
            [CONTENT_TYPE]: APPLICATION_JSON,
        };
    }
    if (!preparedConfig.headers[CONTENT_TYPE]) {
        preparedConfig.headers[CONTENT_TYPE] = APPLICATION_JSON;
    }

    if (!preparedConfig.baseURL) {
        preparedConfig.baseURL = `https://${window.location.host}`;
    }
    if (Array.isArray(preparedConfig.pathParam)) {
        preparedConfig.url += preparedConfig.pathParam.join('/');
    }
    if (typeof preparedConfig.pathParam === 'string' || typeof preparedConfig.pathParam === 'number') {
        preparedConfig.url += `/${preparedConfig.pathParam}`;
    }
    if (preparedConfig.url in inboundTransformConfig) {
        preparedConfig.transformResponse = inboundTransformConfig[preparedConfig.url];
    }
    if (preparedConfig.url in outboundTransformConfig) {
        preparedConfig.transformRequest = outboundTransformConfig[preparedConfig.url];
    }
    return preparedConfig;
};

const prepareRequest = (config) => {
    const preparedConfig = prepareBaseRequest(config);
    if (!config.method) {
        preparedConfig.method = POST;
    }
    return preparedConfig;
};

const generateAPIErrorResponse = (rawResponse) => {
    try {
        const response = JSON.parse(rawResponse);
        return generateErrorResponse(response.errorMessage, response.errorDetail);
    } catch (parseError) {
        // if we can't parse response then just do the default
    }
    return {};
};

const handleRequestFailure = (error) => {
    // eslint-disable-next-line no-underscore-dangle
    const rawResponse = error.response?.request?._response;
    if (rawResponse) {
        const apiErrorResponse = generateAPIErrorResponse(rawResponse);
        if ('error' in apiErrorResponse) {
            return apiErrorResponse;
        }
    }

    if (error.response) {
        if (error.response.removeStatusCodeFromMessage) {
            return generateErrorResponse(translate('api_error_message'), translate(error.response.message));
        }
        if (error.response.removeStatusCodeFromErrorMessage) {
            return generateErrorResponse(translate('api_error_message'), translate(error.response.data.errorMessage));
        }

        const statusCodeError = ERROR_CODES.find((errorCode) => errorCode.status === error.response.status);
        const statusMessage = statusCodeError
            ? `${statusCodeError.status} - ${translate(statusCodeError.message)}. ${error.response.data.errorMessage || ''}`
            : `${error.response.status}. ${error.response.data.errorMessage || ''}`;
        return generateErrorResponse(translate('api_error_message'), statusMessage);
    }

    if (error.request) {
        return generateErrorResponse(translate('api_error_message'), translate('no_response_received'));
    }

    return generateErrorResponse(translate('api_error_message'), translate('request_setup_failed'));
};

const requestConfigInvalid = (config) => {
    if (!config) {
        return Promise.reject(generateErrorResponse(translate('api_error_message'), translate('invalid_object')));
    }
    if (!config.url) {
        return Promise.reject(generateErrorResponse(translate('api_error_message'), translate('invalid_url')));
    }
    return false;
};

const requestAPI = (config, onSuccess, onFailure) => {
    const preparedRequestConfig = prepareRequest(config);
    // axios by default will reject any promises if status codes are >= 200 && < 300
    return axios.request(preparedRequestConfig)
        .then((response) => {
            // the server response will dictate how data is shaped, if we get text/html axios will return that as a string
            if (response?.headers[CONTENT_TYPE] === 'text/html' || isHTML(response.data)) {
                const error = new Error();
                error.response = {
                    removeStatusCodeFromMessage: true,
                    message: 'html_response',
                };
                return Promise.reject(error);
            }
            return isFunction(onSuccess) ? onSuccess(response) : response;
        }).catch((error) => (isFunction(onFailure) ? onFailure(error) : Promise.reject(handleRequestFailure(error))));
};

// apiRequest is flexible in that you can send in axios config objects with a method property
// specifying the HTTP verb you want, it's very flexible
export const apiRequest = async (config, onSuccess, onFailure) => {
    const error = requestConfigInvalid(config);
    if (error) {
        return error;
    }

    return requestAPI(config, onSuccess, onFailure);
};

const postAPI = (data, config, onSuccess, onFailure) => {
    const preparedRequestConfig = prepareBaseRequest(config);
    return axios.post(`${preparedRequestConfig.baseURL}${preparedRequestConfig.url}`, data, preparedRequestConfig)
        .then((response) => (isFunction(onSuccess) ? onSuccess(response) : response))
        .catch((error) => {
            if (error?.response && onFailure && !isFunction(onFailure)) {
                // eslint-disable-next-line no-param-reassign
                error.response.removeStatusCodeFromErrorMessage = true;
            }
            return isFunction(onFailure) ? onFailure(error) : Promise.reject(handleRequestFailure(error));
        });
};

// For certain requests, axios.request() using post doesn't work as intended with responses
// that have a header of Content-Type: multipart/form-data
// so post is implemented in here to get around that
// using apiRequest is encouraged but apiPost is there to back you up if request isn't cutting it
export const apiPost = async (data, config, onSuccess, onFailure) => {
    const error = requestConfigInvalid(config);
    if (error) {
        return error;
    }

    return postAPI(data, config, onSuccess, onFailure);
};
